import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:'',slug:'',excerpt:'',content:'',category_id:'',tags:'',is_published:false,author:'',position:0 });
  const [coverFile, setCoverFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [catName, setCatName] = useState('');

  const fetch = useCallback(() => {
    Promise.all([api.get('/blog/posts'), api.get('/blog/categories')])
      .then(([p,c]) => { setPosts(p.data); setCategories(c.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => { setForm({ title:'',slug:'',excerpt:'',content:'',category_id:'',tags:'',is_published:false,author:'',position:0 }); setCoverFile(null); setEditing(null); setShowForm(false); };
  const handleEdit = (p) => { setEditing(p); setForm({ title:p.title||'',slug:p.slug||'',excerpt:p.excerpt||'',content:p.content||'',category_id:p.category_id||'',tags:p.tags||'',is_published:p.is_published==='true'||p.is_published===true,author:p.author||'',position:parseInt(p.position)||0 }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Başlık gerekli'); return; }
    setIsSubmitting(true);
    try {
      let cover = editing?.cover_image || '';
      if (coverFile) { const fd = new FormData(); fd.append('file', coverFile); const r = await api.post('/uploads', fd); cover = r.data.path; }
      const payload = { ...form, cover_image: cover };
      if (editing) { await api.put(`/blog/posts/${editing.id}`, payload); toast.success('Yazı güncellendi'); }
      else { await api.post('/blog/posts', payload); toast.success('Yazı oluşturuldu'); }
      resetForm(); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Silmek istediğinize emin misiniz?')) return; try { await api.delete(`/blog/posts/${id}`); toast.success('Silindi'); fetch(); } catch { toast.error('Hata'); } };
  const handleAddCat = async () => { if (!catName.trim()) return; try { await api.post('/blog/categories', { name: catName }); toast.success('Kategori eklendi'); setCatName(''); setShowCatForm(false); fetch(); } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); } };
  const handleDeleteCat = async (id) => { try { await api.delete(`/blog/categories/${id}`); fetch(); } catch { } };

  return (
    <div data-testid="admin-blog-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Haber Yönetimi</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowCatForm(!showCatForm)} className="px-3 py-2 rounded-lg border border-input text-sm hover:bg-secondary">Kategoriler</button>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium"><Plus className="w-4 h-4" /> Yeni Yazı</button>
        </div>
      </div>

      {showCatForm && (
        <div className="mb-6 p-4 rounded-xl bg-card border border-border">
          <h3 className="font-heading text-sm font-semibold mb-3">Kategoriler</h3>
          <div className="flex gap-2 mb-3">
            <input value={catName} onChange={e => setCatName(e.target.value)} placeholder="Kategori adı" className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" />
            <button onClick={handleAddCat} className="px-4 py-2 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium">Ekle</button>
          </div>
          <div className="flex flex-wrap gap-2">{categories.map(c => (
            <span key={c.id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary text-xs">{c.name}
              <button onClick={() => handleDeleteCat(c.id)} className="text-destructive hover:text-destructive/80">&times;</button>
            </span>
          ))}</div>
        </div>
      )}

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Yazı Düzenle' : 'Yeni Haber'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Inp label="Başlık *" value={form.title} onChange={v => setForm(f=>({...f,title:v}))} />
              <Inp label="Slug" value={form.slug} onChange={v => setForm(f=>({...f,slug:v}))} placeholder="otomatik oluşturulur" />
              <div><label className="block text-sm font-medium mb-1.5">Kategori</label>
                <select value={form.category_id} onChange={e => setForm(f=>({...f,category_id:e.target.value}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm">
                  <option value="">Kategori seçin</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <Inp label="Yazar" value={form.author} onChange={v => setForm(f=>({...f,author:v}))} />
              <Inp label="Etiketler" value={form.tags} onChange={v => setForm(f=>({...f,tags:v}))} placeholder="virgül ile ayırın" />
              <Inp label="Sıra" type="number" value={form.position} onChange={v => setForm(f=>({...f,position:parseInt(v)||0}))} />
            </div>
            <div><label className="block text-sm font-medium mb-1.5">Özet</label>
              <textarea value={form.excerpt} onChange={e => setForm(f=>({...f,excerpt:e.target.value}))} rows={2} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none" />
            </div>
            <div><label className="block text-sm font-medium mb-1.5">İçerik</label>
              <textarea value={form.content} onChange={e => setForm(f=>({...f,content:e.target.value}))} rows={8} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none" />
            </div>
            <div><label className="block text-sm font-medium mb-1.5">Kapak Görseli</label>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary"><Upload className="w-4 h-4" /> Görsel Seç<input type="file" accept="image/*" className="hidden" onChange={e => setCoverFile(e.target.files[0])} /></label>
              <span className="text-xs text-muted-foreground ml-2">{coverFile?.name || ''}</span>
              {editing?.cover_image && <img src={getFileUrl(editing.cover_image)} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={e => setForm(f=>({...f,is_published:e.target.checked}))} className="rounded" /> Yayınla</label>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="admin-save-button">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      : posts.length === 0 ? <div className="text-center py-10 text-muted-foreground">Henüz haber yok</div>
      : <div className="space-y-3">{posts.map(p => (
        <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
          <div className="w-20 h-14 rounded-lg overflow-hidden bg-secondary shrink-0">{p.cover_image && <img src={getFileUrl(p.cover_image)} alt="" className="w-full h-full object-cover" />}</div>
          <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">{p.title}</div><div className="text-xs text-muted-foreground">{p.category_name || 'Kategorisiz'} | {p.author || 'Anonim'}</div></div>
          <span className={`px-2 py-0.5 text-xs rounded-full ${p.is_published==='true'||p.is_published===true ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>{p.is_published==='true'||p.is_published===true ? 'Yayında' : 'Taslak'}</span>
          <div className="flex gap-1">
            <button onClick={() => handleEdit(p)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}</div>}
    </div>
  );
}

const Inp = ({ label, value, onChange, type='text', placeholder }) => (
  <div><label className="block text-sm font-medium mb-1.5">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" />
  </div>
);
